import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook for making modals draggable and resizable
 * @param {Object} options
 * @param {number} options.minWidth - Minimum width (default: 400)
 * @param {number} options.minHeight - Minimum height (default: 300)
 * @param {number} options.maxWidth - Maximum width (optional)
 * @param {number} options.maxHeight - Maximum height (optional)
 * @param {number} options.initialWidth - Initial width (default: 800)
 * @param {number} options.initialHeight - Initial height (default: 600)
 */
export function useDraggableResizable(options = {}) {
    const {
        minWidth = 400,
        minHeight = 300,
        maxWidth,
        maxHeight,
        initialWidth = 800,
        initialHeight = 600,
    } = options;

    const [position, setPosition] = useState(null);
    const [size, setSize] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState(null);

    const dragStartPos = useRef({ x: 0, y: 0 });
    const elementStartPos = useRef({ x: 0, y: 0 });
    const elementStartSize = useRef({ width: 0, height: 0 });
    const modalRef = useRef(null);

    // Store the user's intended size (before window constraints)
    const userIntendedSize = useRef(null);

    // Initialize position and size on first render
    const initializeModal = useCallback(() => {
        if (modalRef.current && !position) {
            // Calculate initial size respecting window bounds
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const width = Math.min(initialWidth, windowWidth - 32);
            const height = Math.min(initialHeight, windowHeight - 32);

            // Center the modal
            const centerX = (windowWidth - width) / 2;
            const centerY = (windowHeight - height) / 2;

            setPosition({ x: centerX, y: Math.max(20, centerY) });
            setSize({ width, height });
        }
    }, [position, initialWidth, initialHeight]);

    // Handle drag start
    const handleDragStart = useCallback((e) => {
        if (e.button !== 0) return; // Only left click
        e.preventDefault();

        if (!position && modalRef.current) {
            const rect = modalRef.current.getBoundingClientRect();
            setPosition({ x: rect.left, y: rect.top });
            elementStartPos.current = { x: rect.left, y: rect.top };
        } else if (position) {
            elementStartPos.current = { ...position };
        }

        dragStartPos.current = { x: e.clientX, y: e.clientY };
        setIsDragging(true);
    }, [position]);

    // Handle resize start
    const handleResizeStart = useCallback((e, direction) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        if (modalRef.current) {
            const rect = modalRef.current.getBoundingClientRect();
            if (!position) {
                setPosition({ x: rect.left, y: rect.top });
                elementStartPos.current = { x: rect.left, y: rect.top };
            } else {
                elementStartPos.current = { ...position };
            }
            elementStartSize.current = { width: rect.width, height: rect.height };
            if (!size) {
                setSize({ width: rect.width, height: rect.height });
            }
        }

        dragStartPos.current = { x: e.clientX, y: e.clientY };
        setResizeDirection(direction);
        setIsResizing(true);
    }, [position, size]);

    // Handle window resize - adjust modal size/position to stay within bounds
    useEffect(() => {
        const handleWindowResize = () => {
            if (position) {
                const maxW = window.innerWidth - 32;
                const maxH = window.innerHeight - 32;

                const targetWidth = userIntendedSize.current?.width || size?.width;
                const targetHeight = userIntendedSize.current?.height || size?.height;

                if (targetWidth && targetHeight) {
                    const newWidth = Math.min(targetWidth, maxW);
                    const newHeight = Math.min(targetHeight, maxH);

                    const newX = Math.max(16, Math.min(position.x, window.innerWidth - newWidth - 16));
                    const newY = Math.max(16, Math.min(position.y, window.innerHeight - newHeight - 16));

                    if (size && (newWidth !== size.width || newHeight !== size.height)) {
                        setSize({ width: newWidth, height: newHeight });
                    }
                    if (newX !== position.x || newY !== position.y) {
                        setPosition({ x: newX, y: newY });
                    }
                }
            }
        };

        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [size, position]);

    // Handle mouse move
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                const deltaX = e.clientX - dragStartPos.current.x;
                const deltaY = e.clientY - dragStartPos.current.y;

                const rect = modalRef.current.getBoundingClientRect();
                const modalWidth = rect.width;
                const modalHeight = rect.height;

                // Constrain to window bounds
                const newX = Math.max(0, Math.min(window.innerWidth - modalWidth, elementStartPos.current.x + deltaX));
                const newY = Math.max(0, Math.min(window.innerHeight - modalHeight, elementStartPos.current.y + deltaY));

                setPosition({ x: newX, y: newY });
            }

            if (isResizing && resizeDirection) {
                let newWidth = elementStartSize.current.width;
                let newHeight = elementStartSize.current.height;
                let newX = elementStartPos.current.x;
                let newY = elementStartPos.current.y;

                // Fixed edges at start of resize
                const rightEdge = elementStartPos.current.x + elementStartSize.current.width;
                const bottomEdge = elementStartPos.current.y + elementStartSize.current.height;

                // Resize from right edge
                if (resizeDirection.includes('e')) {
                    const mouseX = Math.min(window.innerWidth, Math.max(0, e.clientX));
                    const calculatedWidth = mouseX - elementStartPos.current.x;
                    newWidth = Math.max(minWidth, calculatedWidth);
                }

                // Resize from left edge
                if (resizeDirection.includes('w')) {
                    const mouseX = Math.max(0, Math.min(rightEdge - minWidth, e.clientX));
                    newX = mouseX;
                    newWidth = rightEdge - mouseX;
                }

                // Resize from bottom edge
                if (resizeDirection.includes('s')) {
                    const mouseY = Math.min(window.innerHeight, Math.max(0, e.clientY));
                    const calculatedHeight = mouseY - elementStartPos.current.y;
                    newHeight = Math.max(minHeight, calculatedHeight);
                }

                // Resize from top edge
                if (resizeDirection.includes('n')) {
                    const mouseY = Math.max(0, Math.min(bottomEdge - minHeight, e.clientY));
                    newY = mouseY;
                    newHeight = bottomEdge - mouseY;
                }

                setSize({ width: newWidth, height: newHeight });
                setPosition({ x: newX, y: newY });
            }
        };

        const handleMouseUp = () => {
            // Store the user's intended size when they finish resizing
            if (isResizing && modalRef.current) {
                const rect = modalRef.current.getBoundingClientRect();
                userIntendedSize.current = { width: rect.width, height: rect.height };
            }
            setIsDragging(false);
            setIsResizing(false);
            setResizeDirection(null);
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
            document.body.style.cursor = isDragging ? 'grabbing' :
                resizeDirection === 'se' || resizeDirection === 'nw' ? 'nwse-resize' :
                resizeDirection === 'sw' || resizeDirection === 'ne' ? 'nesw-resize' :
                resizeDirection === 'e' || resizeDirection === 'w' ? 'ew-resize' :
                resizeDirection === 'n' || resizeDirection === 's' ? 'ns-resize' : 'default';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
    }, [isDragging, isResizing, resizeDirection, minWidth, minHeight]);

    // Reset on close
    const reset = useCallback(() => {
        setPosition(null);
        setSize(null);
        userIntendedSize.current = null;
    }, []);

    const modalStyle = position ? {
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: 'none',
        ...(size ? { width: size.width, height: size.height } : {}),
        ...(maxWidth ? { maxWidth } : {}),
        ...(maxHeight ? { maxHeight } : {}),
    } : {
        width: initialWidth,
        height: initialHeight,
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 32px)',
    };

    return {
        modalRef,
        modalStyle,
        position,
        size,
        isDragging,
        isResizing,
        handleDragStart,
        handleResizeStart,
        initializeModal,
        reset,
    };
}

/**
 * Resize handles component
 */
export function ResizeHandles({ onResizeStart }) {
    const handleClass = "absolute opacity-0 hover:opacity-100 transition-opacity z-10";
    const cornerSize = "w-4 h-4";
    const edgeThickness = "4px";

    return (
        <>
            {/* Corners */}
            <div
                className={`${handleClass} ${cornerSize} -top-1 -left-1 cursor-nw-resize`}
                onMouseDown={(e) => onResizeStart(e, 'nw')}
            >
                <div className="absolute top-1 left-1 w-2 h-2 border-t-2 border-l-2 border-cabaza-purple rounded-tl" />
            </div>
            <div
                className={`${handleClass} ${cornerSize} -top-1 -right-1 cursor-ne-resize`}
                onMouseDown={(e) => onResizeStart(e, 'ne')}
            >
                <div className="absolute top-1 right-1 w-2 h-2 border-t-2 border-r-2 border-cabaza-purple rounded-tr" />
            </div>
            <div
                className={`${handleClass} ${cornerSize} -bottom-1 -left-1 cursor-sw-resize`}
                onMouseDown={(e) => onResizeStart(e, 'sw')}
            >
                <div className="absolute bottom-1 left-1 w-2 h-2 border-b-2 border-l-2 border-cabaza-purple rounded-bl" />
            </div>
            <div
                className={`${handleClass} ${cornerSize} -bottom-1 -right-1 cursor-se-resize`}
                onMouseDown={(e) => onResizeStart(e, 'se')}
            >
                <div className="absolute bottom-1 right-1 w-2 h-2 border-b-2 border-r-2 border-cabaza-purple rounded-br" />
            </div>

            {/* Edges */}
            <div
                className={`${handleClass} top-2 -left-1 bottom-2 cursor-ew-resize`}
                style={{ width: edgeThickness }}
                onMouseDown={(e) => onResizeStart(e, 'w')}
            />
            <div
                className={`${handleClass} top-2 -right-1 bottom-2 cursor-ew-resize`}
                style={{ width: edgeThickness }}
                onMouseDown={(e) => onResizeStart(e, 'e')}
            />
            <div
                className={`${handleClass} -top-1 left-2 right-2 cursor-ns-resize`}
                style={{ height: edgeThickness }}
                onMouseDown={(e) => onResizeStart(e, 'n')}
            />
            <div
                className={`${handleClass} -bottom-1 left-2 right-2 cursor-ns-resize`}
                style={{ height: edgeThickness }}
                onMouseDown={(e) => onResizeStart(e, 's')}
            />
        </>
    );
}

export default useDraggableResizable;
